import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Media } from '../../lib/media/media-contract.js';

/**
 * The images in a folder (or the unfiled view), every thumbnail lazy and never the full image.
 * The full size is fetched only by `MediaLightbox`, which this grid does not mount itself — the
 * page owns that, because the lightbox needs to page through the whole loaded set, not just one
 * grid's worth.
 */
export interface GalleryMediaGridProps {
  items: readonly Media[];
  selectable: boolean;
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (mediaId: string) => void;
  onOpen: (mediaId: string) => void;
}

export function GalleryMediaGrid({
  items,
  selectable,
  selectedIds,
  onToggleSelect,
  onOpen,
}: GalleryMediaGridProps): ReactElement {
  const { t } = useTranslation();

  return (
    <ul
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
      data-testid="gallery-media-grid"
    >
      {items.map((item) => {
        const checked = selectedIds.has(item.id);
        const name = item.caption ?? item.originalFilename;
        return (
          <li
            key={item.id}
            className="relative overflow-hidden rounded-md border border-border"
            data-testid="gallery-media-item"
          >
            <button
              type="button"
              className="block aspect-square w-full"
              onClick={() => (selectable ? onToggleSelect(item.id) : onOpen(item.id))}
              aria-label={t('media.action.view', { name })}
            >
              {item.thumbnailUrl ? (
                <img
                  src={item.thumbnailUrl}
                  alt={name}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center bg-muted" />
              )}
            </button>
            {selectable ? (
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggleSelect(item.id)}
                className="absolute left-1.5 top-1.5 size-4"
                aria-label={t('gallery.select.item', { name })}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
